import { XMarkIcon } from '@heroicons/react/16/solid';
import {
  ExclamationTriangleIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/solid';
import { useState } from 'react';
import { Modal } from 'react-daisyui';
import { Trans, useTranslation } from 'react-i18next';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook.ts';
import { useWhatsappApiContext } from 'src/pages/WhatsappApi/WhatsappApi.context.ts';
import { useIntercomContext } from 'src/providers/IntercomProvider/useIntercomContext.hook.ts';

// Explainer video shown on count-based spam banners (LOW/HIGH_SPAMMING) so the
// user understands why the warning appeared and how to avoid escalation. It
// plays inside an in-app modal (YouTube embed) so the user never leaves
// Leadsales.
const SPAM_EXPLAINER_VIDEO_ID = 'D3ZuckNrngE';
const SPAM_EXPLAINER_VIDEO_EMBED = `https://www.youtube.com/embed/${SPAM_EXPLAINER_VIDEO_ID}?autoplay=1&rel=0`;
const SPAM_EXPLAINER_VIDEO_WATCH = `https://www.youtube.com/watch?v=${SPAM_EXPLAINER_VIDEO_ID}`;

// INT-1101: a violation banner's CTA either opens the Intercom support
// widget or links out to an external Meta/Leadsales resource. The i18n key
// lives on the CTA itself so a config can never pair a `cta` with a missing
// or mismatched `ctaKey`.
type tViolationCta =
  | { kind: 'intercom'; key: string }
  | { kind: 'link'; href: string; key: string };

type tBannerConfig = {
  bgColor: string;
  borderColor: string;
  textColor: string;
  iconColor: string;
  titleKey: string;
  messageKey: string;
  linkHref?: string;
  hasVideo?: boolean;
  cta?: tViolationCta;
};

const BANNER_STYLES: Record<'LOW_SPAMMING' | 'HIGH_SPAMMING' | 'RESTRICTED', tBannerConfig> = {
  LOW_SPAMMING: {
    bgColor: 'bg-amber-50',
    borderColor: 'border-l-amber-400',
    textColor: 'text-amber-900',
    iconColor: 'text-amber-500',
    titleKey: 'page.general.account_health.low_spamming.title',
    messageKey: 'page.general.account_health.low_spamming.message',
    linkHref: 'https://faq.whatsapp.com/933578044281252/?locale=es_LA',
    hasVideo: true,
  },
  HIGH_SPAMMING: {
    bgColor: 'bg-red-50',
    borderColor: 'border-l-red-500',
    textColor: 'text-red-900',
    iconColor: 'text-red-500',
    titleKey: 'page.general.account_health.high_spamming.title',
    messageKey: 'page.general.account_health.high_spamming.message',
    linkHref: '',
    hasVideo: true,
  },
  RESTRICTED: {
    bgColor: 'bg-red-50',
    borderColor: 'border-l-red-500',
    textColor: 'text-red-900',
    iconColor: 'text-red-500',
    titleKey: 'page.general.account_health.restricted.title',
    messageKey: 'page.general.account_health.restricted.message',
    linkHref: 'https://www.facebook.com/business/help',
    hasVideo: false,
  },
};

const ERROR_SCHEME = {
  bgColor: 'bg-red-50',
  borderColor: 'border-l-red-500',
  textColor: 'text-red-900',
  iconColor: 'text-red-500',
};

const WARNING_SCHEME = {
  bgColor: 'bg-amber-50',
  borderColor: 'border-l-amber-400',
  textColor: 'text-amber-900',
  iconColor: 'text-amber-500',
};

// INT-1101 — Meta violation_type → banner mapping. Copy/severity/CTA defined
// in INT-1040; do not redesign, only extend with new violation_type values
// Meta introduces. Mirrors whatsapp-business-service's `ViolationCopy.go`
// (ES-only, used for push notifications) — if that table changes, update
// the titleKey/messageKey/cta copy and severity here (and in es/en/br
// locale json) to match.
const VIOLATION_BANNER_STYLES: Record<string, tBannerConfig> = {
  SPAM: {
    ...ERROR_SCHEME,
    titleKey: 'page.general.account_health.violations.spam.title',
    messageKey: 'page.general.account_health.violations.spam.message',
    cta: { kind: 'intercom', key: 'page.general.account_health.violations.spam.cta' },
  },
  SCAM: {
    ...ERROR_SCHEME,
    titleKey: 'page.general.account_health.violations.scam.title',
    messageKey: 'page.general.account_health.violations.scam.message',
    cta: {
      kind: 'link',
      href: 'https://www.facebook.com/help',
      key: 'page.general.account_health.violations.scam.cta',
    },
  },
  TOBACCO: {
    ...ERROR_SCHEME,
    titleKey: 'page.general.account_health.violations.tobacco.title',
    messageKey: 'page.general.account_health.violations.tobacco.message',
    cta: {
      kind: 'link',
      href: 'https://business.whatsapp.com/policy/preview?lang=es_LA',
      key: 'page.general.account_health.violations.tobacco.cta',
    },
  },
  SCRIPTED_BM: {
    ...WARNING_SCHEME,
    titleKey: 'page.general.account_health.violations.scripted_bm.title',
    messageKey: 'page.general.account_health.violations.scripted_bm.message',
    cta: { kind: 'intercom', key: 'page.general.account_health.violations.scripted_bm.cta' },
  },
  USER_INITIATED_CALLS_LOW_PICKUP_RATE: {
    ...WARNING_SCHEME,
    titleKey:
      'page.general.account_health.violations.low_pickup_rate.title',
    messageKey:
      'page.general.account_health.violations.low_pickup_rate.message',
    cta: { kind: 'intercom', key: 'page.general.account_health.violations.low_pickup_rate.cta' },
  },
};

// Fallback for a violation_type Meta introduces that we haven't mapped yet.
// Never falls back to the generic count-based HIGH_SPAMMING copy.
const VIOLATION_BANNER_FALLBACK: tBannerConfig = {
  ...WARNING_SCHEME,
  titleKey: 'page.general.account_health.violations.fallback.title',
  messageKey: 'page.general.account_health.violations.fallback.message',
  cta: {
    kind: 'link',
    href: 'https://business.facebook.com/business/loginpage',
    key: 'page.general.account_health.violations.fallback.cta',
  },
};

export default function AccountHealthBanner() {
  const { t } = useTranslation('whatsappApi/whatsappApi');
  const { canUserAccessWhatsAppApi } = useACLContext();
  const { accountHealthStatus, violationType } = useWhatsappApiContext();
  const { methods: intercomMethods } = useIntercomContext();
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  if (!canUserAccessWhatsAppApi()) return null;
  if (accountHealthStatus === 'OK') return null;

  // A meta_violation-sourced HIGH_SPAMMING carries a violation_type; the
  // count-based HIGH_SPAMMING (spam_count) does not, and keeps its existing
  // generic banner + explainer video untouched.
  const config: tBannerConfig | undefined =
    accountHealthStatus === 'HIGH_SPAMMING' && violationType
      ? (VIOLATION_BANNER_STYLES[violationType] ?? VIOLATION_BANNER_FALLBACK)
      : BANNER_STYLES[accountHealthStatus];
  if (!config) return null;

  return (
    <div
      className={`${config.bgColor} ${config.textColor} ${config.borderColor} border-l-4 rounded-lg p-4 flex items-start gap-3`}
    >
      <ExclamationTriangleIcon
        className={`${config.iconColor} w-5 h-5 flex-shrink-0 mt-0.5`}
      />
      <div>
        <p className="font-semibold text-sm">
          {t(config.titleKey)}
          {config.hasVideo && (
            <button
              type="button"
              onClick={() => setIsVideoOpen(true)}
              className={`${config.textColor} ml-2 inline-flex items-center gap-1 align-middle text-xs font-semibold underline underline-offset-2 transition-opacity hover:opacity-80`}
            >
              <PlayCircleIcon className={`${config.iconColor} w-3.5 h-3.5`} />
              {t('page.general.account_health.watch_video')}
            </button>
          )}
        </p>
        <p className="text-sm mt-1 opacity-90">
          {config.linkHref ? (
            <Trans
              i18nKey={config.messageKey}
              ns="whatsappApi/whatsappApi"
              components={[
                <a
                  href={config.linkHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-semibold"
                />,
              ]}
            />
          ) : (
            t(config.messageKey)
          )}
        </p>
        {config.cta && (
          <p className="text-sm mt-2">
            {config.cta.kind === 'intercom' ? (
              <button
                type="button"
                onClick={() => intercomMethods.showNewMessage()}
                className={`${config.textColor} underline font-semibold transition-opacity hover:opacity-80`}
              >
                {t(config.cta.key)}
              </button>
            ) : (
              <a
                href={config.cta.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`${config.textColor} underline font-semibold transition-opacity hover:opacity-80`}
              >
                {t(config.cta.key)}
              </a>
            )}
          </p>
        )}
      </div>

      {config.hasVideo && (
        <Modal.Legacy
          open={isVideoOpen}
          onClickBackdrop={() => setIsVideoOpen(false)}
          className="max-w-3xl p-0 overflow-hidden bg-black"
        >
          <button
            type="button"
            aria-label={t('page.general.account_health.close_video')}
            onClick={() => setIsVideoOpen(false)}
            className="absolute top-2 right-2 z-10 rounded-full bg-black/60 p-1 text-white transition-colors hover:bg-black/80"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          {/* Mount the iframe only while open so the video autoplays on open
              and stops (unmounts) on close. */}
          {isVideoOpen && (
            <div className="aspect-video w-full">
              <iframe
                className="w-full h-full"
                src={SPAM_EXPLAINER_VIDEO_EMBED}
                title={t(config.titleKey)}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          {/* Fallback for users whose browser/extension blocks the embed. */}
          <p className="bg-black px-4 py-2 text-center text-xs text-white/70">
            <Trans
              i18nKey="page.general.account_health.video_fallback"
              ns="whatsappApi/whatsappApi"
              components={[
                <a
                  href={SPAM_EXPLAINER_VIDEO_WATCH}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-white underline"
                />,
              ]}
            />
          </p>
        </Modal.Legacy>
      )}
    </div>
  );
}
